import { Link, Redirect, useLocation, useParams } from "react-router-dom";
import { DASHBOARD_LINKS } from "utils/dashboardLinks";
import {
  AMC_QUERY_EXPLORER_BREADCRUMB_SPEC,
  CANNED_QUERIES,
  FEATURED_REPORT_TYPES_WITH_DATE_SUPPORT,
  FEATURED_REPORTS,
} from "./utils";
import { useMemo, useRef } from "react";
import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import Table from "views/merchandise/tables/BulkActionsTable";
import { useAppSelector } from "redux/hooks";
import { buildTextColumn } from "views/merchandise/columns/buildTextColumn";
import moment from "moment";
import Card from "components/core/basic/Card";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import { buildDateColumn } from "views/merchandise/columns/buildDateColumn";
import { trellisPalette } from "components/custom/analytics/palettes";
import Loading from "components/core/blocks/Loading";
import { toTitleCase } from "utils/formatText";
import axios from "axios";
import getURLPrefix from "utils/getUrlPrefix";
import FileSaver from "file-saver";
import { useAMCReportData } from "./hooks/useAMCReportData";
import { useAMCQueryData } from "./hooks/useAMCQueryData";
import { useModuleEnabled } from "redux/selectors/modules";
import { PageContainer } from "../../components/core/basic/PageContainer";
import { HStack, VStack } from "../../../styled-system/jsx";
import { css } from "../../../styled-system/css";
import GetReportButton from "components/core/basic/AMCGetReportButton";

const API_URL = getURLPrefix();

export const formatSubscriptionName = (query) => {
  let displayName = [...CANNED_QUERIES, ...FEATURED_REPORTS].find(
    (cannedQuery) => cannedQuery.name === query?.query_type || cannedQuery.name === query?.name,
  )?.display_name;

  if ("frequency" in query) {
    displayName += `: ${toTitleCase(query.frequency)} Frequency`;
  }

  if (typeof query.parameters === "object" && "days" in query.parameters) {
    displayName += `, ${query.parameters.days} Day Lookback`;
  }

  return displayName;
};

const AMCReportsListing = ({ overrideId, fullPage = true }) => {
  const { id: queryId } = useParams();
  const { pathname } = useLocation();

  const subscriptionId = overrideId ? overrideId : queryId;

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const hasAMCModule = useModuleEnabled("AMC");
  const isPathToPurchaseReportIndex =
    pathname.includes("conversionchart") || overrideId === "pathToPurchaseBaseFeaturedReport";

  const { data: queryData, isLoading } = useAMCQueryData();

  const queryDetails = useMemo(() => {
    if (isPathToPurchaseReportIndex) {
      return FEATURED_REPORTS.find((report) => report.name === "PATH_TO_PURCHASE_GROUPS_INTERNAL");
    }
    const querySubscription =
      queryData?.find((subscribedQuery) => subscribedQuery.id === parseInt(subscriptionId)) ?? {};
    const queryDetails = [...CANNED_QUERIES, ...FEATURED_REPORTS].find(
      (query) => query.name === querySubscription?.query_type,
    );

    return { ...queryDetails, ...querySubscription };
  }, [queryData, subscriptionId, isPathToPurchaseReportIndex]);

  const queryOptions = useMemo(() => {
    const subscribedReportOptions =
      queryData?.map((query) => {
        const displayName = formatSubscriptionName(query);
        return { label: displayName, value: query.id };
      }) ?? [];

    const featuredReportOptions =
      FEATURED_REPORTS?.map((reportType) => {
        if (FEATURED_REPORT_TYPES_WITH_DATE_SUPPORT.includes(reportType.name)) {
          return { label: reportType.display_name, value: reportType.href, featured: true };
        }
      })?.filter((reportTypeOption) => reportTypeOption) ?? [];

    return [...subscribedReportOptions, ...featuredReportOptions];
  }, [queryData]);

  const TABLE_ID = `cannedAMCQueryReports${queryDetails?.query_type}`;
  const tableRef = useRef();
  const filterProps = useCustomTableFilters(TABLE_ID);

  const queryType = isPathToPurchaseReportIndex ? "PATH_TO_PURCHASE_GROUPS_INTERNAL" : queryDetails?.query_type;

  const { reportMetadata, isLoading: isReportDataLoading } = useAMCReportData(null, queryType, false);

  const reportData = useMemo(() => {
    const formattedReportData = reportMetadata?.map((report) => {
      return {
        ...report,
        start_date: report.metadata.timeWindowStart,
        end_date: report.metadata.timeWindowEnd,
        created_on: report.created_on,
        parameters: report.metadata.parameterValues,
      };
    });

    if (queryDetails.name === "PATH_TO_PURCHASE_GROUPS_INTERNAL") {
      if (queryDetails.id) {
        return formattedReportData?.filter((report) => report.subscription === queryDetails.id);
      }

      return formattedReportData?.filter((report) => !report.subscription);
    }

    return formattedReportData;
  }, [queryDetails.id, queryDetails.name, reportMetadata]);

  const breadCrumbItems = useMemo(() => {
    const breadCrumbSpec = [
      {
        name: "AMC Dashboard",
        href: "/user/amc/dashboard",
        unsorted: true,
        dropdownLinkOptions: DASHBOARD_LINKS,
        dropdownSubtitle: "Reports",
        activeOption: "AMC Dashboard",
      },
      {
        name: formatSubscriptionName(queryDetails),
        href: queryDetails?.featured ? queryDetails?.href : `/user/amc/dashboard/reports/${subscriptionId}`,
        dropdownLinkOptions: queryOptions
          ?.map((query) => {
            const { label, value, featured } = query;

            return {
              name: label,
              href: featured ? value : `/user/amc/dashboard/reports/${value}`,
            };
          })
          .filter((q) => !!q.name),
        searchable: true,
        searchPlaceholderText: "Search subscribed queries...",
        dropdownSubtitle: "Subscribed Queries",
      },
    ];

    if (!breadCrumbSpec[0].dropdownLinkOptions?.find((o) => o.name === AMC_QUERY_EXPLORER_BREADCRUMB_SPEC.name)) {
      breadCrumbSpec[0].dropdownLinkOptions.push(AMC_QUERY_EXPLORER_BREADCRUMB_SPEC);
    }

    return breadCrumbSpec;
  }, [subscriptionId, queryDetails, queryOptions]);

  const handleDownload = async (id, title, fileType) => {
    const url = `${API_URL}/api/exports/${id}/download/`;
    const response = await axios({
      method: "GET",
      url: url,
      responseType: "blob",
    });

    const blobType =
      {
        xlsx: "vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8",
        csv: "text/csv;charset=utf-8",
      }?.[fileType] ?? "text;charset=utf-8";

    const blob = new Blob([response.data], {
      type: blobType,
    });

    FileSaver.saveAs(blob, `${title}.${fileType}`);
  };

  const columnSpecs = useMemo(
    () => [
      buildDateColumn("start_date", "Start Date", {
        formatter: (v) => moment(v).format("MMMM D, YYYY"),
      }),
      buildDateColumn("end_date", "End Date", {
        formatter: (v) => moment(v).format("MMMM D, YYYY"),
      }),
      buildTextColumn("frequency", "Frequency", {
        formatter: () => {
          return <span>{toTitleCase(queryDetails?.frequency)}</span>;
        },
        hideFilter: true,
        width: 100,
        checked: !isPathToPurchaseReportIndex,
        toolTip: "Daily: Every Day",
      }),
      buildDateColumn("created_on", "Created", {
        formatter: (v) => moment(v).format("MMMM D, YYYY"),
        width: 150,
      }),
      {
        key: "actions",
        compareKey: null,
        options: {
          metric: {
            id: "actions",
            format: "text",
            accessor: (d) => d,
            name: "Actions",
            width: "300px",
          },
          formatter: (row) => {
            const {
              export: { id: exportId, name: exportName, file_type: exportFileType },
              id: reportId,
            } = row ?? {};
            return (
              <div className="pe-1">
                <Link
                  style={{ color: trellisPalette[13], fontWeight: 600 }}
                  to={`/user/amc/dashboard/reports/details/${reportId}`}
                  className="me-5"
                >
                  View Report
                </Link>
                <span
                  style={{ cursor: "pointer", color: trellisPalette[13], fontWeight: 600, textDecoration: "underline" }}
                  onClick={() => {
                    handleDownload(exportId, exportName, exportFileType);
                  }}
                >
                  Download
                </span>
              </div>
            );
          },
        },
      },
    ],
    [queryDetails, isPathToPurchaseReportIndex],
  );

  const TableComponent = () => (
    <Table
      marketPlace={marketPlace}
      tableRef={tableRef}
      tableId={TABLE_ID}
      hideFilters={true}
      hideSearchFilter={true}
      additionalFilters={null}
      excludedFilterColumns={[]}
      {...filterProps}
      data={reportData}
      columnSpecs={columnSpecs}
      tableRowDescription={"Reports"}
      defaultSorted={[{ id: "start_date", desc: true }]}
      isLoading={isReportDataLoading}
      filterWidget={true}
      handleBulkChanges={() => {}}
      minRows={1}
      isSelectable={false}
      isExpandable={false}
    />
  );

  if (!hasAMCModule) {
    return <Redirect to="/user/dashboard" />;
  }

  if (!fullPage) {
    return <TableComponent />;
  }

  return (
    <>
      <BreadcrumbDates items={breadCrumbItems} hideDateSelector={true} />

      {isLoading || isReportDataLoading ?
        <Loading fullPage={true} />
      : <PageContainer withBreadcrumb>
          <VStack
            maxWidth="1300px"
            alignItems={"stretch"}
            width="100%"
            paddingX="xxlarge"
            paddingY="large"
            alignSelf={"center"}
          >
            <h4
              className={css({
                margin: 0,
                fontWeight: "bold",
                textAlign: "center",
              })}
            >
              {queryDetails.display_name} Query Reports
            </h4>

            <Card>
              <TableComponent />
              {!["PATH_TO_PURCHASE_CAMPAIGNS_INTERNAL", "PATH_TO_PURCHASE_GROUPS_INTERNAL"].includes(
                queryDetails.name,
              ) && (
                <HStack justifyContent={"center"} marginTop={"large"}>
                  <GetReportButton queryDetails={queryDetails} marketPlace={marketPlace} />
                </HStack>
              )}
            </Card>
          </VStack>
        </PageContainer>
      }
    </>
  );
};

export default AMCReportsListing;
